// Mock data for the trading interface. Will be replaced with real SpacetimeDB data later.

export interface MockOrderBookEntry {
  price: number;
  amount: number;
  total: number;
}

export interface MockPricePoint {
  time: number; // unix timestamp
  price: number;
}

export interface MockOrder {
  id: string;
  pair: string;
  swapping: string;
  swappingToken: string;
  receiving: string;
  receivingToken: string;
  avgFillPrice: string;
  status: "open" | "filled" | "partial" | "cancelled";
  fees: string;
  orderType: "market" | "limit";
  datePlaced: string;
}

export interface MockTokenInfo {
  name: string;
  ticker: string;
  description: string;
  registered: string;
  policyId: string;
  divisible: boolean;
  decimals: number;
  dateCreated: string;
}

export interface MockBalance {
  token: string;
  amount: string;
}

// Seeded random for deterministic mock data across renders
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

// Generate a realistic jagged price history (like the Figma design)
function generatePriceHistory(): MockPricePoint[] {
  const rng = seededRandom(42);
  const points: MockPricePoint[] = [];
  const now = Math.floor(Date.now() / 1000);
  const totalPoints = 200;
  let price = 44.5;

  for (let i = totalPoints; i >= 0; i--) {
    // Multi-frequency noise for realistic jagged movement
    const trend = Math.sin(i * 0.03) * 1.5;
    const mid = Math.sin(i * 0.12) * 0.6;
    const noise = (rng() - 0.5) * 0.8;
    const spike = rng() < 0.05 ? (rng() - 0.5) * 1.5 : 0;

    price = 44 + trend + mid + noise + spike;
    price = Math.max(41.5, Math.min(46.5, price));

    points.push({
      time: now - i * 90,
      price: Number(price.toFixed(2)),
    });
  }
  return points;
}

// Generate order book entries with more realistic values (matching Figma scale)
function generateAsks(): MockOrderBookEntry[] {
  const rng = seededRandom(101);
  const prices = [3.8555, 3.85, 3.8499, 3.8499, 3.8333, 3.8201, 3.8150, 3.8110, 3.85, 3.8555];
  const amounts = [600_000, 3_000_000, 2_215_000, 100_000, 889_351, 553_135, 200_000, 200_000, 600_000, 3_000_000];
  const entries: MockOrderBookEntry[] = [];
  let cumulative = 0;

  for (let i = 0; i < 10; i++) {
    const amount = amounts[i] || Math.floor(100_000 + rng() * 3_000_000);
    cumulative += amount;
    entries.push({
      price: prices[i] || 3.85 + rng() * 0.02,
      amount,
      total: cumulative,
    });
  }
  return entries.reverse();
}

function generateBids(): MockOrderBookEntry[] {
  const rng = seededRandom(202);
  const prices = [2.9195, 3.8110, 3.8150, 3.8201, 3.8333, 3.8499, 3.8499, 3.85, 3.8555, 3.8499];
  const amounts = [1_779_051, 200_000, 200_000, 553_135, 889_351, 100_000, 2_215_000, 3_000_000, 600_000, 600_000];
  const entries: MockOrderBookEntry[] = [];
  let cumulative = 0;

  for (let i = 0; i < 10; i++) {
    const amount = amounts[i] || Math.floor(100_000 + rng() * 3_000_000);
    cumulative += amount;
    entries.push({
      price: prices[i] || 3.82 - rng() * 0.02,
      amount,
      total: cumulative,
    });
  }
  return entries;
}

export const mockPriceHistory = generatePriceHistory();

export const mockOrderBook = {
  asks: generateAsks(),
  bids: generateBids(),
  spread: 0.096906,
  spreadPercent: 0.04,
};

export const mockTokenInfo: MockTokenInfo = {
  name: "SUNDAE",
  ticker: "SUNDAE",
  description:
    "SundaeSwap is a decentralized exchange and liquidity provision protocol. SundaeSwap DAO Token allows holders to participate in SundaeSwap governance and additional features introduced by the DAO.",
  registered: "CIP-26",
  policyId: "9a9693a9a37912a5097918f97918d15240c92ab729a0b7c4aa144d77",
  divisible: true,
  decimals: 6,
  dateCreated: "Sept 13, 2024 • 09:20:15 PM EDT",
};

export const mockBalances: MockBalance[] = [
  { token: "ADA", amount: "1,000.00" },
  { token: "SUNDAE", amount: "18,391.633996" },
];

export const mockOpenOrders: MockOrder[] = [
  {
    id: "ord-001",
    pair: "SUNDAE/ADA",
    swapping: "500.00",
    swappingToken: "ADA",
    receiving: "37,174.72",
    receivingToken: "SUNDAE",
    avgFillPrice: "0.01345",
    status: "open",
    fees: "1.50 ADA",
    orderType: "limit",
    datePlaced: "2026-03-17 09:23",
  },
  {
    id: "ord-002",
    pair: "SUNDAE/ADA",
    swapping: "10,000.00",
    swappingToken: "SUNDAE",
    receiving: "134.50",
    receivingToken: "ADA",
    avgFillPrice: "0.01345",
    status: "filled",
    fees: "0.40 ADA",
    orderType: "market",
    datePlaced: "2026-03-17 08:45",
  },
  {
    id: "ord-003",
    pair: "SUNDAE/ADA",
    swapping: "250.00",
    swappingToken: "ADA",
    receiving: "18,587.36",
    receivingToken: "SUNDAE",
    avgFillPrice: "0.01345",
    status: "partial",
    fees: "0.75 ADA",
    orderType: "limit",
    datePlaced: "2026-03-16 22:10",
  },
  {
    id: "ord-004",
    pair: "SUNDAE/ADA",
    swapping: "5,000.00",
    swappingToken: "SUNDAE",
    receiving: "67.25",
    receivingToken: "ADA",
    avgFillPrice: "0.01345",
    status: "cancelled",
    fees: "0.00 ADA",
    orderType: "limit",
    datePlaced: "2026-03-16 18:30",
  },
];

export const currentPrice = 2.81951;
export const priceChange24h = 4.57;
export const priceUsd = 0.001357;
